import { XMarkIcon } from "@heroicons/react/24/outline";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { type VariantProps, cva } from "class-variance-authority";
import * as React from "react";
import { Button, type ButtonProps } from "src/components/ui/Button";
import { cn } from "src/utils/tailwind";

const Dialog = DialogPrimitive.Root;

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = DialogPrimitive.Portal;

const DialogClose = DialogPrimitive.Close;

const DialogOverlay = React.forwardRef<
	React.ElementRef<typeof DialogPrimitive.Overlay>,
	React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
	<DialogPrimitive.Overlay
		ref={ref}
		className={cn(
			"fixed inset-0 z-50 bg-gray-300 opacity-50 origin-center data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
			className,
		)}
		{...props}
	/>
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

const dialogContentVariants = cva(
	[
		"fixed left-[50%] top-[50%] z-50 grid w-full translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background p-6 shadow-lg duration-200 max-h-[90vh] overflow-y-auto",
		"data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
		"data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
		"sm:rounded-lg",
	],
	{
		variants: {
			size: {
				sm: "max-w-sm",
				md: "max-w-lg",
				lg: "max-w-2xl",
				xl: "max-w-4xl",
				full: "max-w-[95vw]",
			},
		},
		defaultVariants: {
			size: "md",
		},
	},
);

const DialogContent = React.forwardRef<
	React.ElementRef<typeof DialogPrimitive.Content>,
	React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> &
		VariantProps<typeof dialogContentVariants> & {
			hideCloseButton?: boolean;
			focus?: boolean;
		}
>(({ className, children, size, hideCloseButton, focus, ...props }, ref) => (
	<DialogPortal>
		<DialogOverlay />
		<DialogPrimitive.Content
			ref={ref}
			className={cn(dialogContentVariants({ size }), className, {
				"focus:outline-none": !focus,
			})}
			{...props}
		>
			{children}
			{!hideCloseButton && (
				<DialogPrimitive.Close className="absolute right-4 top-4 rounded-xs opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-hidden focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground">
					<XMarkIcon
						className="h-5 w-5"
						data-testid="dialog-close-button"
					/>
					<span className="sr-only">Close</span>
				</DialogPrimitive.Close>
			)}
		</DialogPrimitive.Content>
	</DialogPortal>
));
DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogHeader = ({
	className,
	...props
}: React.HTMLAttributes<HTMLDivElement>) => (
	<div
		className={cn(
			"flex flex-col gap-1.5 text-center sm:text-left",
			className,
		)}
		{...props}
	/>
);
DialogHeader.displayName = "DialogHeader";

const DialogFooter = ({
	className,
	...props
}: React.HTMLAttributes<HTMLDivElement>) => (
	<div
		className={cn(
			"-m-6 mt-0 flex bg-background-light py-4 px-6 flex-row justify-end space-x-2 rounded-b-lg",
			className,
		)}
		{...props}
	/>
);
DialogFooter.displayName = "DialogFooter";

const DialogTitle = React.forwardRef<
	React.ElementRef<typeof DialogPrimitive.Title>,
	React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
	<DialogPrimitive.Title
		ref={ref}
		className={cn(
			"text-lg font-medium leading-none tracking-tight pr-2",
			className,
		)}
		{...props}
	/>
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
	React.ElementRef<typeof DialogPrimitive.Description>,
	React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
	<DialogPrimitive.Description
		ref={ref}
		className={cn("text-sm text-muted-foreground", className)}
		{...props}
	/>
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

const DialogConfirmation = ({
	className,
	onConfirm,
	onCancel,
	cancelText,
	confirmText,
	matomoEventCategory,
	matomoEventName,
	...props
}: ButtonProps & {
	onConfirm?: () => void;
	onCancel: () => void;
	cancelText: string;
	confirmText?: string;
	matomoEventCategory?: string;
	matomoEventName?: string;
}) => (
	<div className={cn("flex gap-4", className)}>
		<Button
			data-testid="confirmation-cancel"
			variant="default"
			onClick={onCancel}
			data-matomo-action="click-event"
			data-matomo-category={matomoEventCategory}
			data-matomo-event="click"
			data-matomo-name={`${matomoEventName}-cancel`}
		>
			{cancelText}
		</Button>
		{(onConfirm || props.type === "submit") && confirmText && (
			<Button
				data-testid="confirmation-confirm"
				onClick={onConfirm}
				data-matomo-action="click-event"
				data-matomo-category={matomoEventCategory}
				data-matomo-event="click"
				data-matomo-name={`${matomoEventName}-confirm`}
				{...props}
			>
				{confirmText}
			</Button>
		)}
	</div>
);
DialogConfirmation.displayName = "DialogConfirmation";

export {
	Dialog,
	DialogClose,
	DialogConfirmation,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogOverlay,
	DialogPortal,
	DialogTitle,
	DialogTrigger,
};
